// components/BasicModal.js
import React, { useState, useEffect, useContext } from 'react';
import { UserAuth } from '../context/AuthContext';
import { AppContextisChecked } from '../context/AppContextisChecked';
import { auth, database } from '../firebase/firebaseConfig';
import { initializeUserInDatabase } from '../utils/api-helpers';
// Subscription plan data
const subscriptionPlans = [
  {
    id: 'free',
    title: "Free",
    price: "0",
    description: "Limited tasks",
    buttonText: "Your current plan",
    buttonStyle: 'disabled',
    features: [
      { 
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        ), 
        text: 'Limited free queries in a lifetime' 
      },
      // { icon: '🖼️', text: 'Limited and slower image generation' },
      // { icon: '🔍', text: 'Limited deep research' },
      // { icon: '💾', text: 'Limited memory and context' },
    ],
    // footer: 'Have an existing plan? See billing help'
  },
  {
    id: 'pro',
    title: "Pro",
    price: `${process.env.REACT_APP_PAYMENT}`,
    description: "Full access to the best of LLMs",
    buttonText: "Get Pro",
    buttonStyle: 'black',
    features: [
      { 
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 3h12l4 6-10 13L2 9z"></path>
            <path d="M11 3L8 9l4 13"></path>
            <path d="M13 3l3 6-4 13"></path>
          </svg>
        ), 
        text: 'Unlimited queries for an entire 30 days' 
      },
      // { icon: '💬', text: 'Unlimited messages and uploads' },
      // { icon: '🖼️', text: 'Unlimited and faster image creation' },
      // { icon: '🔍', text: 'Maximum deep research and agent mode' },
      { 
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
        ), 
        text: 'Maximum memory and context' 
      },
      // { icon: '📊', text: 'Expanded projects, tasks, and custom GPTs' },
      // { icon: '🎬', text: 'Expanded Sora video generation' },
      // { icon: '🤖', text: 'Expanded Codex agent' },
      { 
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        ), 
        text: 'Research preview of new features' 
      },
    ],
    // footer: 'I need help with a billing issue\n. Learn more'
  }
];

// Custom Google Button Component
const GoogleButton = ({ onClick, disabled, style, isMobile }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '8px' : '12px',
      width: '100%',
      padding: isMobile ? '10px 12px' : '12px 16px',
      backgroundColor: style?.backgroundColor || 'white',
      border: style?.border || '1px solid #dadce0',
      borderRadius: '4px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '500',
      color: style?.color || '#3c4043',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      backdropFilter: style?.backdropFilter || 'none',
      ...style
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        if (style?.backgroundColor === 'rgba(255, 255, 255, 0.1)') {
          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
          e.target.style.boxShadow = '0 1px 3px 0 rgba(255,255,255,0.1)';
        } else {
          e.target.style.backgroundColor = '#f8f9fa';
          e.target.style.boxShadow = '0 1px 2px 0 rgba(60,64,67,0.3)';
        }
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled) {
        e.target.style.backgroundColor = style?.backgroundColor || 'white';
        e.target.style.boxShadow = 'none';
      }
    }}
  >
    <svg width={isMobile ? "16" : "18"} height={isMobile ? "16" : "18"} viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
    Sign in with Google
  </button>
);

export default function BasicModal({ openModal, setOpenModal, initialView = 'auth-choice', requireAuth = false }) {
  const [open, setOpen] = useState(false);
  const [modalView, setModalView] = useState(initialView);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullname, setFullName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [userId, setUserId] = useState("");
  const [activeTab, setActiveTab] = useState('personal');
  const [forceOpen, setForceOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { googleSignIn, user, authError, sessionTerminated, userRequestData, loading: authLoading } = UserAuth();
  const { auth: isAuth, setAuth } = useContext(AppContextisChecked);
  const { unpaid, setUnpaid } = useContext(AppContextisChecked);
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine if user can close the modal
  const canClose = !requireAuth || (user && user.uid);

  // Force modal open if authentication is required and no user
  useEffect(() => {
    if (requireAuth && !user && !authLoading) {
      setForceOpen(true);
      setOpen(true);
      setModalView('auth-choice');
    } else if (user && requireAuth) {
      setForceOpen(false);
    }
  }, [requireAuth, user, authLoading]);

  // Make modal opener functions globally available
  useEffect(() => {
    window.openAuthModal = () => {
      setOpenModal(true);
      setModalView('auth-choice');
    };

    window.openSubscriptionModal = () => {
      setOpenModal(true);
      setModalView('subscription');
    };

    return () => {
      delete window.openAuthModal;
      delete window.openSubscriptionModal;
    };
  }, [setOpenModal]);

  // Check if Firebase services are available
  useEffect(() => {
    if (!auth) {
      console.error("Firebase Auth is not initialized!");
      setError("Authentication service is not available. Please check Firebase configuration.");
    } else {
      // // console.log("Firebase Auth is available:", auth);
    }

    if (!database) {
      console.error("Firebase Database is not initialized!");
    } else {
      // // console.log("Firebase Database is available:", database);
    }
  }, []);

  // Auto-close modal when user is authenticated (only if auth was required)
  useEffect(() => {
    if (user && user.uid && requireAuth) {
      // // console.log("User authenticated, closing modal automatically");

      setTimeout(() => {
        setOpen(false);
        setOpenModal(false);
        setForceOpen(false);
        setModalView(initialView);
        setError("");
        setEmail("");
        setPassword("");
        setFullName("");
      }, 500);
    }
  }, [user, requireAuth, setOpenModal, initialView]);

  // Handle modal open state
  useEffect(() => {
    if (openModal || forceOpen) {
      setOpen(true);

      if (requireAuth && !user) {
        setModalView('auth-choice');
      } else if (openModal) {
        setModalView(initialView);
      }
    } else if (!forceOpen) {
      setOpen(false);
    }
  }, [openModal, forceOpen, initialView, requireAuth, user]);

  // ESC key prevention when auth is required
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && requireAuth && !user) {
        event.preventDefault();
        event.stopPropagation();
        setError("Please sign in to continue using the application");
        setTimeout(() => setError(""), 3000);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscKey, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey, true);
    };
  }, [open, requireAuth, user]);

  // Prevent clicking outside to close when auth is required
  const handleBackdropClick = () => {
    if (requireAuth && !user) {
      setError("Please sign in to continue using the application");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (canClose && !forceOpen) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (requireAuth && !user) {
      setError("Please sign in to continue using the application");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (forceOpen) {
      return;
    }

    setOpen(false);
    setOpenModal(false);
    setModalView(initialView);
    setError("");
    setEmail("");
    setPassword("");
    setFullName("");
  };

  const handleAuthSuccess = async () => {
    setAuth(true);

    const userData = await initializeUserInDatabase();
    if (userData) {
      if (userData.isNewUser) {
        // // console.log(`Welcome! You have ${process.env.REACT_APP_FREE_REQ} free requests to try our service.`);
      } else if (userData.freeRequestsRemaining > 0 && !userData.hasSubscription) {
        // // console.log(`Welcome back! You have ${userData.freeRequestsRemaining} free request${userData.freeRequestsRemaining > 1 ? 's' : ''} remaining.`);
      }
    }

    if (!auth) {
      console.error("Auth not available");
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No current user after auth success");
      return;
    }

    const userRef = database.ref("users/" + currentUser.uid);
    userRef.once('value').then((snapshot) => {
      const userData = snapshot.val();

      if (userData && userData.subscription && userData.subscription.status === 'active') {
        // // console.log("User has active subscription, closing modal");
        setTimeout(() => {
          setOpen(false);
          setOpenModal(false);
          setModalView(initialView);
          setError("");
          setEmail("");
          setPassword("");
          setFullName("");
        }, 300);
      } else if (modalView === 'register') {
        // // console.log("New user registration, showing subscription options");
        setModalView('subscription');
      } else {
        // // console.log("Existing user without subscription, closing modal");
        setTimeout(() => {
          setOpen(false);
          setOpenModal(false);
          setModalView(initialView);
          setError("");
          setEmail("");
          setPassword("");
          setFullName("");
        }, 300);
      }
    }).catch((error) => {
      console.error("Error checking subscription:", error);
      setTimeout(() => {
        setOpen(false);
        setOpenModal(false);
        setModalView(initialView);
        setError("");
        setEmail("");
        setPassword("");
        setFullName("");
      }, 300);
    });
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError("");

      // // console.log("=== Google Sign-in Debug ===");
      // // console.log("1. Firebase auth initialized?", !!auth);
      // // console.log("2. Current domain:", window.location.hostname);
      // // console.log("3. Protocol:", window.location.protocol);

      const result = await googleSignIn();

      // // console.log("4. Sign-in result:", result);

      if (result && result.pending) {
        // console.log("5. Redirect pending...");
        return;
      }

      if (result && result.user) {
        // console.log("6. Success! User:", result.user.email);
        await handleAuthSuccess();
      }
    } catch (error) {
      console.error("=== Sign-in Error ===");
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      console.error("Full error:", error);

      setAuth(false);
      setError(error.message || "Authentication failed");
    } finally {
      if (!sessionStorage.getItem('authRedirectPending')) {
        setLoading(false);
      }
    }
  };

  const handleEmailLogin = async () => {
    setLoading(true);
    setError("");

    try {
      if (!auth) {
        setError("Authentication service is not available. Please try again.");
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError("Please provide a valid email");
        setLoading(false);
        return;
      }

      // console.log("Attempting email login for:", email);
      const response = await auth.signInWithEmailAndPassword(email, password);

      if (response.user) {
        const userData = await initializeUserInDatabase();
        if (userData && userData.freeRequestsRemaining > 0 && !userData.hasSubscription) {
          // console.log(`Welcome back! You have ${userData.freeRequestsRemaining} free request${userData.freeRequestsRemaining > 1 ? 's' : ''} remaining.`);
        }

        setEmail("");
        setPassword("");
        handleAuthSuccess();
      }
    } catch (error) {
      console.error("Login Error:", error);

      if (error.code === 'auth/user-not-found') {
        setError("No account found with this email address.");
      } else if (error.code === 'auth/wrong-password') {
        setError("Incorrect password. Please try again.");
      } else if (error.code === 'auth/invalid-email') {
        setError("Invalid email address format.");
      } else if (error.code === 'auth/configuration-not-found') {
        setError("Authentication service is not properly configured. Please contact support.");
      } else {
        setError(error.message || "Login failed. Please check your credentials.");
      }
      setAuth(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setError("");

    try {
      if (!auth) {
        setError("Authentication service is not available. Please try again.");
        setLoading(false);
        return;
      }

      if (!email || !fullname || !password) {
        setError("Please fill all the fields");
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError("Please provide a valid email");
        setLoading(false);
        return;
      }

      if (password.length < 6) {
        setError("Password must be at least 6 characters");
        setLoading(false);
        return;
      }

      // console.log("Attempting to register user:", email);
      const response = await auth.createUserWithEmailAndPassword(email, password);

      if (response.user) {
        await response.user.updateProfile({
          displayName: fullname
        });

        const uid = response.user.uid;
        const userRef = database.ref('users/' + uid);
        await userRef.set({
          uid: uid,
          email: email,
          username: fullname,
          createdAt: new Date().toISOString(),
          numberOfRequests: 0
        });

        const userData = await initializeUserInDatabase();
        if (userData) {
          // console.log('Welcome! You have 3 free requests to try our service.');
        }

        setFullName("");
        setEmail("");
        setPassword("");
        setAuth(true);

        setModalView('subscription');
      }
    } catch (error) {
      console.error("Register error:", error);

      if (error.code === 'auth/email-already-in-use') {
        setError("An account with this email already exists.");
      } else if (error.code === 'auth/invalid-email') {
        setError("Invalid email address format.");
      } else if (error.code === 'auth/weak-password') {
        setError("Password should be at least 6 characters.");
      } else if (error.code === 'auth/configuration-not-found') {
        setError("Authentication service is not properly configured. Please contact support.");
      } else {
        setError(error.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const checkUserSubscription = () => {
    if (!auth) {
      console.error("Auth not available in checkUserSubscription");
      return;
    }

    auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
        if (!database) {
          console.error("Database not available");
          return;
        }

        const userRef = database.ref("users/" + user.uid);
        userRef.on('value', (snapshot) => {
          const userData = snapshot.val();
          if (userData && userData.subscription) {
            setUserSubscription(userData.subscription);

            if (userData.subscription.status === 'active' && userData.subscription.planType) {
              // console.log("Active subscription detected, ensuring modal closes");
              if (open) {
                setTimeout(() => {
                  setOpen(false);
                  setOpenModal(false);
                  setModalView(initialView);
                }, 300);
              }
            }
          }
        });
      }
    });
  };

  useEffect(() => {
    checkUserSubscription();
  }, []);

  const handleCheckout = async (plan) => {
    setLoading(true);
    try {
      if (!user || !userId) {
        setError('Please sign in before subscribing');
        setModalView('auth-choice');
        setLoading(false);
        return;
      }

      const idToken = await auth.currentUser.getIdToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/v1/create-subscription-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        mode: "cors",
        body: JSON.stringify({
          plan: Number(plan.price)
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.warning) {
          console.warn("Firebase warning:", data.warning);
        }
        window.location = data.session.url;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Checkout failed");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setError(error.message || "Failed to create checkout session");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (modalView) {
      case 'auth-choice':
        return (
          <div style={{
            maxWidth: isMobile ? '100%' : '510px',
            width: '100%',
            margin: '0 auto',
            position: 'relative'
          }}>
            {/* Canvas background effect */}
            <canvas 
              width="250"
              height="60"
              style={{
                position: 'absolute',
                inset: 0,
                height: '100%',
                width: '100%',
                borderRadius: '23px',
                pointerEvents: 'none'
              }}
            />
            
            {/* Gradient overlay */}
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              top: 0,
              overflow: 'hidden',
              borderRadius: '23px',
              pointerEvents: 'none'
            }}>
              <div style={{
                width: '320px',
                height: '320px',
                background: 'linear-gradient(to right, #FF6B6B, #4ECDC4, #45B7D1)',
                aspectRatio: '1',
                opacity: 0.15,
                position: 'absolute',
                top: 0,
                filter: 'blur(32px)',
                borderRadius: '50%',
                transform: 'translateY(-50%)'
              }} />
            </div>

            {/* Main content container with dark theme */}
            <div style={{
              background: 'radial-gradient(ellipse at bottom, #1b2735 0%, #090a0f 100%)',
              // borderRadius: '23px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: isMobile ? '24px 20px' : '32px 24px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
            }}>
              {/* Star Layer 1: Chaotic zigzag pattern */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.8), transparent),
                  radial-gradient(1px 1px at 80px 60px, #ffffff, transparent),
                  radial-gradient(2px 2px at 140px 100px, rgba(255,255,255,0.6), transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '200px 150px',
                opacity: 1,
                animation: 'randomMove1 345s ease-in-out infinite, twinkle1 4.2s linear infinite',
                pointerEvents: 'none'
              }} />
              
              {/* Star Layer 2: Swirling motion */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(3px 3px at 60px 90px, #ffffff, transparent),
                  radial-gradient(2px 2px at 120px 40px, #ffffff, transparent),
                  radial-gradient(1px 1px at 180px 110px, rgba(255,255,255,0.9), transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '250px 200px',
                opacity: 1,
                // animation: 'randomMove2 10060s cubic-bezier(0.4, 0.0, 0.6, 1) infinite, twinkleBright 2.5s ease-in-out infinite',
                pointerEvents: 'none'
              }} />
              
              {/* Star Layer 3: Figure-8 pattern */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(1px 1px at 40px 70px, rgba(255,255,255,0.7), transparent),
                  radial-gradient(2px 2px at 100px 30px, #ffffff, transparent),
                  radial-gradient(1px 1px at 160px 90px, rgba(255,255,255,0.5), transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '200px 150px',
                opacity: 0.8,
                animation: 'randomMove3 320s linear infinite, twinkle1 3.2s linear infinite',
                pointerEvents: 'none'
              }} />
              
              {/* Star Layer 4: Random bouncing */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(2px 2px at 30px 50px, rgba(255,255,255,0.6), transparent),
                  radial-gradient(3px 3px at 90px 120px, #ffffff, transparent),
                  radial-gradient(1px 1px at 170px 80px, rgba(255,255,255,0.8), transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '220px 170px',
                opacity: 0.9,
                animation: 'randomMove4 240s ease-in-out infinite, twinkle2 4.5s ease-in-out infinite',
                pointerEvents: 'none'
              }} />
              
              {/* Star Layer 5: Spiral motion */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(1px 1px at 50px 20px, #ffffff, transparent),
                  radial-gradient(2px 2px at 110px 80px, rgba(255,255,255,0.7), transparent),
                  radial-gradient(3px 3px at 190px 140px, #ffffff, transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '240px 180px',
                opacity: 0.7,
                animation: 'randomMove5 115s cubic-bezier(0.25, 0.46, 0.45) infinite, twinkleBright 2.5s ease-in-out infinite',
                pointerEvents: 'none'
              }} />
              
              {/* Star Layer 6: Erratic movement */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(2px 2px at 70px 40px, rgba(255,255,255,0.9), transparent),
                  radial-gradient(1px 1px at 130px 100px, #ffffff, transparent),
                  radial-gradient(2px 2px at 200px 60px, rgba(255,255,255,0.6), transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '260px 160px',
                opacity: 0.85,
                animation: 'randomMove6 125s linear infinite, twinkle1 2s linear infinite',
                pointerEvents: 'none'
              }} />
              
              {/* Star Layer 7: Wave-like motion */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(3px 3px at 45px 65px, #ffffff, transparent),
                  radial-gradient(2px 2px at 155px 35px, #ffffff, transparent),
                  radial-gradient(1px 1px at 95px 115px, rgba(255,255,255,0.8), transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '200px 150px',
                opacity: 1,
                // animation: 'randomMove7 10095s ease-in-out infinite, twinkleBright 2.8s ease-in-out infinite',
                pointerEvents: 'none'
              }} />

              {requireAuth && !user && (
                <div style={{
                  background: 'rgba(254, 243, 199, 0.1)',
                  border: 'white',
                  borderRadius: '6px',
                  padding: isMobile ? '10px' : '12px',
                  marginBottom: isMobile ? '12px' : '16px',
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#fbbf24',
                  color: 'white',
                  textAlign: 'center',
                  fontWeight: '500',
                  position: 'relative',
                  zIndex: 1
                }}>
                  🔒 Authentication is required to use this application
                </div>
              )}

              {userRequestData && !userRequestData.hasSubscription && (
                <div style={{
                  background: userRequestData.freeRequestsRemaining > 0 
                    ? 'rgba(240, 253, 244, 0.1)' 
                    : 'rgba(254, 242, 242, 0.1)',
                  border: `1px solid ${userRequestData.freeRequestsRemaining > 0 
                    ? 'rgba(187, 247, 208, 0.3)' 
                    : 'rgba(254, 202, 202, 0.3)'}`,
                  borderRadius: '6px',
                  padding: isMobile ? '10px' : '12px',
                  marginBottom: isMobile ? '12px' : '16px',
                  fontSize: isMobile ? '12px' : '14px',
                  color: userRequestData.freeRequestsRemaining > 0 ? '#4ade80' : '#f87171',
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {userRequestData.freeRequestsRemaining > 0
                    ? `🎁 You have ${userRequestData.freeRequestsRemaining} free request${userRequestData.freeRequestsRemaining > 1 ? 's' : ''} remaining`
                    : '🔒 Free requests exhausted. Please upgrade to continue.'}
                </div>
              )}

              <div style={{
                textAlign: 'center',
                padding: isMobile ? '24px 0 20px' : '48px 0 32px',
                position: 'relative',
                zIndex: 1
              }}>
                <h1 style={{
                  fontSize: isMobile ? '24px' : '32px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  color: '#ffffff',
                  marginBottom: isMobile ? '16px' : '24px',
                  fontWeight: '600',
                  lineHeight: '1.2'
                }}>
                  Welcome to RAGRecruit Legal
                </h1>
                <p style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontSize: isMobile ? '14px' : '16px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: '400',
                  lineHeight: '1.5'
                }}>
                  Log in with your account to continue
                </p>
              </div>

              <div style={{
                maxWidth: '400px',
                margin: '0 auto',
                paddingBottom: isMobile ? '16px' : '24px',
                position: 'relative',
                zIndex: 1
              }}>
                {sessionTerminated && (
                  <div style={{
                    background: 'rgba(254, 243, 199, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    color: '#fbbf24',
                    padding: isMobile ? '10px' : '12px',
                    marginBottom: isMobile ? '12px' : '16px',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px'
                  }} role="alert">
                    <p style={{ fontWeight: '500', marginBottom: '4px' }}>Session expired</p>
                    <p style={{ fontSize: isMobile ? '11px' : '13px' }}>Please sign in again to continue.</p>
                  </div>
                )}

                <div style={{ marginBottom: '12px' }}>
                  <GoogleButton
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    isMobile={isMobile}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      padding: isMobile ? '10px' : '12px',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '500',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      transition: 'all 0.15s ease',
                      color: '#ffffff',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                </div>

                {loading && (
                  <div style={{
                    textAlign: 'center',
                    marginTop: isMobile ? '12px' : '16px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      width: isMobile ? '14px' : '16px',
                      height: isMobile ? '14px' : '16px',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderTopColor: '#ffffff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      marginRight: '8px',
                      verticalAlign: 'middle'
                    }}></span>
                    Authenticating...
                  </div>
                )}

                {error && (
                  <div style={{
                    textAlign: 'center',
                    marginTop: isMobile ? '12px' : '16px',
                    color: '#f87171',
                    fontSize: isMobile ? '12px' : '14px',
                    padding: isMobile ? '10px' : '12px',
                    background: 'rgba(254, 226, 226, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(248, 113, 113, 0.3)'
                  }}>{error}</div>
                )}

                {authError && (
                  <div style={{
                    textAlign: 'center',
                    marginTop: isMobile ? '12px' : '16px',
                    color: '#f87171',
                    fontSize: isMobile ? '12px' : '14px',
                    padding: isMobile ? '10px' : '12px',
                    background: 'rgba(254, 226, 226, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(248, 113, 113, 0.3)'
                  }}>Auth error: {authError.message}</div>
                )}

                <p style={{
                  fontSize: isMobile ? '11px' : '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center',
                  marginTop: isMobile ? '24px' : '32px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  lineHeight: '1.5'
                }}>
                  By continuing, you agree to our{' '}
                  <a href="#" style={{ color: '#4ecdc4', textDecoration: 'none' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" style={{ color: '#4ecdc4', textDecoration: 'none' }}>Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>
        );

      case 'login':
        return (
          <div style={{ padding: isMobile ? '32px 0 16px' : '48px 0 24px' }}>
            <button
              onClick={() => setModalView('auth-choice')}
              style={{
                position: 'absolute',
                left: isMobile ? '16px' : '24px',
                top: isMobile ? '16px' : '24px',
                background: 'none',
                border: 'none',
                color: '#666',
                fontSize: isMobile ? '18px' : '20px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '6px',
                transition: 'background-color 0.15s ease',
                width: isMobile ? '32px' : '36px',
                height: isMobile ? '32px' : '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9f9f9';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              ←
            </button>

            <h2 style={{
              textAlign: 'center',
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '600',
              marginBottom: isMobile ? '24px' : '32px',
              color: '#202123',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              Welcome back
            </h2>

            <div style={{
              maxWidth: isMobile ? '100%' : '400px',
              margin: '0 auto'
            }}>
              <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#202123',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    border: '1px solid #c5c5d2',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '16px',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                    boxSizing: 'border-box',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backgroundColor: '#fff'
                  }}
                  placeholder="you@example.com"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10a37f';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#c5c5d2';
                  }}
                />
              </div>

              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#202123',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    border: '1px solid #c5c5d2',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '16px',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                    boxSizing: 'border-box',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backgroundColor: '#fff'
                  }}
                  placeholder="Enter your password"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10a37f';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#c5c5d2';
                  }}
                />
              </div>

              <button
                onClick={handleEmailLogin}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  backgroundColor: loading ? '#74aa9c' : '#10a37f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = '#0e8d6f';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = '#10a37f';
                  }
                }}
              >
                {loading ? 'Signing in...' : 'Continue'}
              </button>
            </div>

            {error && (
              <div style={{
                maxWidth: isMobile ? '100%' : '400px',
                margin: '16px auto 0',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '6px',
                color: '#d00',
                fontSize: isMobile ? '12px' : '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>{error}</div>
            )}

            <div style={{
              textAlign: 'center',
              marginTop: isMobile ? '24px' : '32px',
              paddingTop: isMobile ? '24px' : '32px',
              borderTop: '1px solid #e5e5e5'
            }}>
              <p style={{
                color: '#666',
                fontSize: isMobile ? '13px' : '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                Don't have an account?{' '}
                <button
                  onClick={() => setModalView('register')}
                  style={{
                    color: '#10a37f',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '500',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.15s ease',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(16, 163, 127, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        );

      case 'register':
        return (
          <div style={{ padding: isMobile ? '32px 0 16px' : '48px 0 24px' }}>
            <button
              onClick={() => setModalView('auth-choice')}
              style={{
                position: 'absolute',
                left: isMobile ? '16px' : '24px',
                top: isMobile ? '16px' : '24px',
                background: 'none',
                border: 'none',
                color: '#666',
                fontSize: isMobile ? '18px' : '20px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '6px',
                transition: 'background-color 0.15s ease',
                width: isMobile ? '32px' : '36px',
                height: isMobile ? '32px' : '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9f9f9';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              ←
            </button>

            <h2 style={{
              textAlign: 'center',
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '600',
              marginBottom: isMobile ? '24px' : '32px',
              color: '#202123',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              Create your account
            </h2>

            <div style={{
              maxWidth: isMobile ? '100%' : '400px',
              margin: '0 auto'
            }}>
              <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#202123',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Name
                </label>
                <input
                  type="text"
                  value={fullname}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    border: '1px solid #c5c5d2',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '16px',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                    boxSizing: 'border-box',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backgroundColor: '#fff'
                  }}
                  placeholder="Enter your full name"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10a37f';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#c5c5d2';
                  }}
                />
              </div>

              <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#202123',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    border: '1px solid #c5c5d2',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '16px',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                    boxSizing: 'border-box',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backgroundColor: '#fff'
                  }}
                  placeholder="you@example.com"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10a37f';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#c5c5d2';
                  }}
                />
              </div>

              <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: '#202123',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    border: '1px solid #c5c5d2',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '16px',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                    boxSizing: 'border-box',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backgroundColor: '#fff'
                  }}
                  placeholder="6+ characters"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10a37f';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#c5c5d2';
                  }}
                />
                <p style={{
                  marginTop: '6px',
                  fontSize: isMobile ? '11px' : '12px',
                  color: '#666',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  Your password must be at least 6 characters
                </p>
              </div>

              <button
                onClick={handleRegister}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  backgroundColor: loading ? '#74aa9c' : '#10a37f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s ease',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = '#0e8d6f';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = '#10a37f';
                  }
                }}
              >
                {loading ? 'Creating account...' : 'Continue'}
              </button>
            </div>

            {error && (
              <div style={{
                maxWidth: isMobile ? '100%' : '400px',
                margin: '16px auto 0',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '6px',
                color: '#d00',
                fontSize: isMobile ? '12px' : '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>{error}</div>
            )}

            <div style={{
              textAlign: 'center',
              marginTop: isMobile ? '24px' : '32px',
              paddingTop: isMobile ? '24px' : '32px',
              borderTop: '1px solid #e5e5e5'
            }}>
              <p style={{
                color: '#666',
                fontSize: isMobile ? '13px' : '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                Already have an account?{' '}
                <button
                  onClick={() => setModalView('login')}
                  style={{
                    color: '#10a37f',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '500',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.15s ease',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(16, 163, 127, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  Log in
                </button>
              </p>
            </div>

            <p style={{
              fontSize: isMobile ? '11px' : '12px',
              color: '#999',
              textAlign: 'center',
              marginTop: isMobile ? '20px' : '24px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: '1.5',
              maxWidth: isMobile ? '100%' : '400px',
              margin: `${isMobile ? '20px' : '24px'} auto 0`
            }}>
              By clicking "Continue", you agree to our{' '}
              <a href="#" style={{ color: '#10a37f', textDecoration: 'none' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="#" style={{ color: '#10a37f', textDecoration: 'none' }}>Privacy Policy</a>
            </p>
          </div>
        );

      case 'subscription':
        return (
          <div style={{
            maxWidth: isMobile ? '100%' : '1000px',
            width: '100%',
            margin: '0 auto',
            padding: isMobile ? '0' : '20px',
            marginTop: isMobile ? '0' : '-5vh',
            backgroundColor: isMobile ? '#f3f3f3' : '#f3f3f3',
          }}>
            <div style={{
              padding: isMobile ? '20px 0' : '20px 0',
              width: '100%',
              margin: '0 auto'
            }}>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '22px' : '32px',
                fontWeight: '600',
                marginBottom: isMobile ? '20px' : '24px',
                color: '#000000',
                paddingTop: isMobile ? '0' : '0'
              }}>
                Upgrade your plan
              </h2>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: isMobile ? '24px' : '40px',
                gap: '0',
                backgroundColor: isMobile ? '#f3f4f6' : 'transparent',
                borderRadius: isMobile ? '20px' : '6px',
                padding: isMobile ? '2px' : '0',
                width: isMobile ? 'fit-content' : 'auto',
                margin: isMobile ? '0 auto 24px' : '0 auto 40px'
              }}>
                <button
                  onClick={() => setActiveTab('personal')}
                  style={{
                    padding: isMobile ? '8px 20px' : '8px 20px',
                    backgroundColor: activeTab === 'personal' ? (isMobile ? 'white' : '#f3f4f6') : 'transparent',
                    border: 'none',
                    borderRadius: isMobile ? '18px' : '6px',
                    fontSize: isMobile ? '14px' : '14px',
                    fontWeight: activeTab === 'personal' ? '500' : '400',
                    color: activeTab === 'personal' ? '#000000' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: activeTab === 'personal' && isMobile ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  Personal
                </button>
                <button
                  onClick={() => setActiveTab('business')}
                  style={{
                    padding: isMobile ? '8px 20px' : '8px 20px',
                    backgroundColor: activeTab === 'business' ? (isMobile ? 'white' : '#f3f4f6') : 'transparent',
                    border: 'none',
                    borderRadius: isMobile ? '18px' : '6px',
                    fontSize: isMobile ? '14px' : '14px',
                    fontWeight: activeTab === 'business' ? '500' : '400',
                    color: activeTab === 'business' ? '#000000' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: activeTab === 'business' && isMobile ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  Business
                </button>
              </div>

              <div style={{
                display: isMobile ? 'flex' : 'grid',
                flexDirection: isMobile ? 'column' : undefined,
                gridTemplateColumns: isMobile ? undefined : 'repeat(2, 1fr)',
                gap: isMobile ? '16px' : '20px',
                marginBottom: isMobile ? '20px' : '40px',
                padding: isMobile ? '0 20px' : '0',
                maxWidth: isMobile ? '400px' : '100%',
                margin: isMobile ? '0 auto 20px' : '0 auto 40px'
              }}>
                {subscriptionPlans.map((plan) => (
                  <div
                    key={plan.id}
                    style={{
                      position: 'relative',
                      border: isMobile ? '1px solid #e5e7eb' : '1px solid #e5e7eb',
                      borderRadius: isMobile ? '16px' : '12px',
                      padding: isMobile ? '24px 20px' : '24px',
                      backgroundColor: 'white',
                      minHeight: isMobile ? 'auto' : '50px',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: isMobile ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {plan.popular && (
                      <div style={{
                        position: 'absolute',
                        top: isMobile ? '-10px' : '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#6366f1',
                        color: 'white',
                        padding: isMobile ? '3px 12px' : '4px 16px',
                        borderRadius: isMobile ? '10px' : '12px',
                        fontSize: isMobile ? '9px' : '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Popular
                      </div>
                    )}

                    <div>
                      <h3 style={{
                        fontSize: isMobile ? '24px' : '20px',
                        fontWeight: '600',
                        color: '#000000',
                        marginBottom: isMobile ? '16px' : '8px'
                      }}>
                        {plan.title}
                      </h3>

                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        marginBottom: isMobile ? '16px' : '12px'
                      }}>
                        <span style={{ 
                          fontSize: isMobile ? '18px' : '24px', 
                          fontWeight: '400',
                          color: '#6b7280'
                        }}>$</span>
                        <span style={{ 
                          fontSize: isMobile ? '40px' : '36px', 
                          fontWeight: '600', 
                          color: '#000000',
                          lineHeight: '1'
                        }}>
                          {plan.price}
                        </span>
                        <span style={{
                          fontSize: isMobile ? '14px' : '14px',
                          color: '#6b7280',
                          marginLeft: '8px'
                        }}>
                          USD /<br />month
                        </span>
                      </div>

                      <p style={{
                        fontSize: isMobile ? '15px' : '14px',
                        color: '#000000',
                        marginBottom: isMobile ? '24px' : '24px',
                        minHeight: isMobile ? 'auto' : '40px',
                        lineHeight: '1.5',
                        fontWeight: '500'
                      }}>
                        {plan.description}
                      </p>

                      {plan.id === 'free' ? (
                        <button
                          disabled
                          style={{
                            width: '100%',
                            padding: isMobile ? '12px 20px' : '10px 24px',
                            backgroundColor: '#f3f4f6',
                            color: '#9ca3af',
                            border: '1px solid #e5e7eb',
                            borderRadius: isMobile ? '25px' : '8px',
                            fontSize: isMobile ? '14px' : '14px',
                            fontWeight: '500',
                            cursor: 'not-allowed',
                            marginBottom: isMobile ? '24px' : '24px'
                          }}
                        >
                          {plan.buttonText}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleCheckout(plan)}
                          disabled={loading}
                          style={{
                            width: '100%',
                            padding: isMobile ? '12px 20px' : '10px 24px',
                            backgroundColor: '#000000',
                            color: 'white',
                            border: 'none',
                            borderRadius: isMobile ? '25px' : '8px',
                            fontSize: isMobile ? '14px' : '14px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s',
                            marginBottom: isMobile ? '24px' : '24px'
                          }}
                          onMouseEnter={(e) => {
                            if (!loading && !isMobile) {
                              e.target.style.backgroundColor = '#333333';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!loading && !isMobile) {
                              e.target.style.backgroundColor = '#000000';
                            }
                          }}
                        >
                          {loading ? 'Processing...' : plan.buttonText}
                        </button>
                      )}

                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        flex: 1
                      }}>
                        {plan.features.map((feature, idx) => (
                          <li key={idx} style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginBottom: isMobile ? '16px' : '12px',
                            fontSize: isMobile ? '15px' : '14px',
                            color: '#000000',
                            lineHeight: '1.5'
                          }}>
                            <span style={{
                              marginRight: isMobile ? '12px' : '8px',
                              flexShrink: 0,
                              marginTop: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#6b7280'
                            }}>
                              {feature.icon}
                            </span>
                            <span style={{
                              fontWeight: '400'
                            }}>{feature.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {plan.footer && (
                      <div style={{
                        marginTop: 'auto',
                        paddingTop: isMobile ? '20px' : '16px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <p style={{
                          fontSize: isMobile ? '12px' : '12px',
                          color: '#6b7280',
                          textAlign: 'left',
                          whiteSpace: 'pre-line',
                          lineHeight: '1.5'
                        }}>
                          {plan.footer.split('\n').map((line, i) => (
                            <span key={i}>
                              {line.includes('billing help') || line.includes('Learn more') ? (
                                <a href="#" style={{ 
                                  color: '#6b7280', 
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '2px'
                                }}>
                                  {line}
                                </a>
                              ) : (
                                line
                              )}
                              {i < plan.footer.split('\n').length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {isMobile && (
                <div style={{
                  textAlign: 'center',
                  // padding: '20px',
                  marginTop: '0'
                }}>
                  <p style={{
                    fontSize: '14px',
                    color: '#4b5563',
                    marginBottom: '8px'
                  }}>
                    👥 Need more capabilities for your business?
                  </p>
                  <span
                    // href="#"
                    style={{
                      fontSize: '14px',
                      color: '#6366f1',
                      // textDecoration: 'underline',
                      fontWeight: '500'
                    }}
                  >
                    Contact us for a custom plan <br/> sanyat.hoque@gmail.com | +1 437 871 4227
                  </span>
                </div>
              )}

              {!isMobile && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginTop: '32px'
                }}>
                  <p style={{
                    fontSize: '14px',
                    color: '#4b5563',
                    marginBottom: '8px'
                  }}>
                    👥 Need more capabilities for your business?
                  </p>
                  <span
                    // href="#"
                    style={{
                      fontSize: '14px',
                      color: '#6366f1',
                      // textDecoration: 'underline',
                      fontWeight: '500'
                    }}
                  >
                    Contact us for a custom plan <br/> sanyat.hoque@gmail.com | +1 437 871 4227
                  </span>
                </div>
              )}

              {error && (
                <p style={{
                  textAlign: 'center',
                  marginTop: isMobile ? '12px' : '16px',
                  color: '#ef4444',
                  fontSize: isMobile ? '13px' : '14px',
                  padding: isMobile ? '10px 16px' : '8px 16px',
                  background: 'rgba(254, 226, 226, 0.5)',
                  borderRadius: '8px',
                  marginLeft: isMobile ? '20px' : '0',
                  marginRight: isMobile ? '20px' : '0'
                }}>{error}</p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Free Tier Notification Box */}
      {/* {unpaid && open && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          animation: 'slideIn 0.3s ease-out',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <span style={{
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center'
          }}>⚠️</span>
          <div>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              color: '#dc2626'
            }}>
              Your Free Tier is over
            </p>
            <p style={{
              margin: '4px 0 0',
              fontSize: '12px',
              color: '#7f1d1d'
            }}>
              Please upgrade to continue using all features
            </p>
          </div>
        </div>
      )} */}

      <div
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: modalView === 'subscription' && isMobile ? 'transparent' : 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: requireAuth && !user ? 'not-allowed' : 'pointer'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: modalView === 'subscription' ? 'fixed' : 'relative',
            top: modalView === 'subscription' ? 0 : 'auto',
            left: modalView === 'subscription' ? 0 : 'auto',
            width: modalView === 'subscription' ? '100vw' : (isMobile ? '96%' : '510px'),
            height: modalView === 'subscription' ? '100vh' : 'auto',
            maxHeight: modalView === 'subscription' ? '100vh' : '90vh',
            maxWidth: modalView !== 'subscription' && isMobile ? '400px' : undefined,
            overflowY: 'auto',
            backgroundColor: modalView === 'subscription' && isMobile ? '#f7f7f8' : (modalView === 'auth-choice' ? 'transparent' : 'white'),
            border: 'none',
            borderRadius: modalView === 'subscription' ? '0' : (isMobile ? '8px' : '12px'),
            boxShadow: modalView === 'subscription' ? 'none' : (modalView === 'auth-choice' ? 'none' : '0 10px 40px rgba(0, 0, 0, 0.15)'),
            outline: 'none',
            display: modalView === 'subscription' ? 'flex' : 'block',
            flexDirection: modalView === 'subscription' ? 'column' : 'unset',
            alignItems: modalView === 'subscription' ? 'center' : 'unset',
            justifyContent: modalView === 'subscription' ? (isMobile ? 'flex-start' : 'center') : 'unset',
            padding: modalView === 'subscription' ? (isMobile ? '0' : '40px 20px') : (modalView === 'auth-choice' ? '0' : (isMobile ? '0 20px' : '0 32px')),
            paddingTop: modalView === 'subscription' && isMobile ? '0' : undefined,
            backgroundColor: modalView === 'subscription' ? '#f3f3f3' : (modalView === 'auth-choice' ? 'transparent' : 'white'),
          }}
        >
          {canClose && !forceOpen && (
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: modalView === 'subscription' && isMobile ? '8px' : (isMobile ? '16px' : '20px'),
                right: modalView === 'subscription' && isMobile ? '8px' : (isMobile ? '16px' : '70px'),
                background: 'transparent',
                border: 'none',
                fontSize: isMobile ? '20px' : '37px',
                color: '#666',
                cursor: 'pointer',
                padding: '4px',
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                zIndex: modalView === 'subscription' ? 10 : 1
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              ×
            </button>
          )}

          {error && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fee2e2',
              color: '#991b1b',
              padding: isMobile ? '6px 12px' : '8px 16px',
              borderRadius: '6px',
              fontSize: isMobile ? '12px' : '14px',
              zIndex: 1002,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              maxWidth: '90%',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {renderContent()}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes twinkle1 {
            0%, 100% { opacity: 0.8; }
            50% { opacity: 0.3; }
          }
          
          @keyframes twinkle2 {
            0%, 100% { opacity: 0.9; }
            30% { opacity: 0.2; }
            60% { opacity: 0.5; }
          }
          
          @keyframes twinkleBright {
            0%, 100% { 
              opacity: 0.2;
              transform: scale(1);
            }
            50% { 
              opacity: 1;
              transform: scale(1.2);
            }
          }
          
          @keyframes randomMove1 {
            0% { transform: translate(0, 0); }
            15% { transform: translate(35px, -45px); }
            30% { transform: translate(-25px, -30px); }
            45% { transform: translate(45px, 20px); }
            60% { transform: translate(-40px, 35px); }
            75% { transform: translate(30px, -25px); }
            90% { transform: translate(-20px, 40px); }
            100% { transform: translate(0, 0); }
          }
          
          @keyframes randomMove2 {
            0% { transform: translate(0, 0) rotate(0deg); }
            12% { transform: translate(-30px, 25px) rotate(45deg); }
            25% { transform: translate(40px, 40px) rotate(90deg); }
            37% { transform: translate(35px, -35px) rotate(135deg); }
            50% { transform: translate(-45px, -20px) rotate(180deg); }
            62% { transform: translate(-35px, 30px) rotate(225deg); }
            75% { transform: translate(25px, -40px) rotate(270deg); }
            87% { transform: translate(20px, 35px) rotate(315deg); }
            100% { transform: translate(0, 0) rotate(360deg); }
          }
          
          @keyframes randomMove3 {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-20px, -30px); }
            20% { transform: translate(30px, -40px); }
            30% { transform: translate(40px, 10px); }
            40% { transform: translate(-10px, 40px); }
            50% { transform: translate(-40px, 20px); }
            60% { transform: translate(-30px, -35px); }
            70% { transform: translate(35px, -20px); }
            80% { transform: translate(25px, 30px); }
            90% { transform: translate(-35px, 15px); }
          }
          
          @keyframes randomMove4 {
            0% { transform: translate(0, 0) scale(1); }
            8% { transform: translate(25px, 35px) scale(0.9); }
            16% { transform: translate(-40px, 25px) scale(1.1); }
            24% { transform: translate(-30px, -40px) scale(0.95); }
            32% { transform: translate(35px, -25px) scale(1.05); }
            40% { transform: translate(40px, 30px) scale(0.9); }
            48% { transform: translate(-25px, 40px) scale(1.1); }
            56% { transform: translate(-35px, -30px) scale(0.95); }
            64% { transform: translate(30px, -35px) scale(1.05); }
            72% { transform: translate(20px, 25px) scale(0.9); }
            80% { transform: translate(-30px, 20px) scale(1.1); }
            88% { transform: translate(25px, -20px) scale(0.95); }
            100% { transform: translate(0, 0) scale(1); }
          }
          
          @keyframes randomMove5 {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            5% { transform: translate(10px, -15px) rotate(36deg); }
            10% { transform: translate(25px, -25px) rotate(72deg); }
            15% { transform: translate(40px, -20px) rotate(108deg); }
            20% { transform: translate(45px, -5px) rotate(144deg); }
            25% { transform: translate(40px, 15px) rotate(180deg); }
            30% { transform: translate(25px, 30px) rotate(216deg); }
            35% { transform: translate(5px, 35px) rotate(252deg); }
            40% { transform: translate(-15px, 30px) rotate(288deg); }
            45% { transform: translate(-30px, 15px) rotate(324deg); }
            50% { transform: translate(-35px, -5px) rotate(360deg); }
            55% { transform: translate(-30px, -25px) rotate(396deg); }
            60% { transform: translate(-15px, -35px) rotate(432deg); }
            65% { transform: translate(5px, -40px) rotate(468deg); }
            70% { transform: translate(25px, -35px) rotate(504deg); }
            75% { transform: translate(40px, -20px) rotate(540deg); }
            80% { transform: translate(45px, 0px) rotate(576deg); }
            85% { transform: translate(35px, 20px) rotate(612deg); }
            90% { transform: translate(20px, 30px) rotate(648deg); }
            95% { transform: translate(5px, 20px) rotate(684deg); }
          }
          
          @keyframes randomMove6 {
            0% { transform: translate(0, 0); }
            5% { transform: translate(-15px, 20px); }
            10% { transform: translate(30px, 15px); }
            15% { transform: translate(25px, -35px); }
            20% { transform: translate(-40px, -25px); }
            25% { transform: translate(-35px, 30px); }
            30% { transform: translate(20px, 40px); }
            35% { transform: translate(40px, -10px); }
            40% { transform: translate(-25px, -40px); }
            45% { transform: translate(-45px, 10px); }
            50% { transform: translate(15px, 35px); }
            55% { transform: translate(35px, -30px); }
            60% { transform: translate(-20px, -35px); }
            65% { transform: translate(-40px, 25px); }
            70% { transform: translate(30px, 30px); }
            75% { transform: translate(45px, -20px); }
            80% { transform: translate(-10px, -30px); }
            85% { transform: translate(-35px, 20px); }
            90% { transform: translate(25px, 25px); }
            95% { transform: translate(20px, -15px); }
            100% { transform: translate(0, 0); }
          }
          
          @keyframes randomMove7 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            6% { transform: translate(15px, -10px) scale(1.05); }
            12% { transform: translate(5px, -25px) scale(0.95); }
            18% { transform: translate(-20px, -30px) scale(1.1); }
            24% { transform: translate(-35px, -15px) scale(0.9); }
            30% { transform: translate(-40px, 10px) scale(1.05); }
            36% { transform: translate(-30px, 30px) scale(0.95); }
            42% { transform: translate(-10px, 40px) scale(1.1); }
            48% { transform: translate(15px, 35px) scale(0.9); }
            54% { transform: translate(35px, 20px) scale(1.05); }
            60% { transform: translate(45px, -5px) scale(0.95); }
            66% { transform: translate(40px, -25px) scale(1.1); }
            72% { transform: translate(25px, -40px) scale(0.9); }
            78% { transform: translate(5px, -45px) scale(1.05); }
            84% { transform: translate(-15px, -35px) scale(0.95); }
            90% { transform: translate(-25px, -15px) scale(1.1); }
            96% { transform: translate(-15px, 5px) scale(0.95); }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(100px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>
    </>
  );
}